import { ICashTransfer } from "@/interfaces/cash-float/cash-float";
import { IUntypedCashPointClosure } from "@/types/db/cashpoint-closure";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { IPendingOrder } from "@/types/db/order-sync";
import { IPendingAnswerSync } from "@/types/db/answer-sync";
import { IPendingPayment } from "@/types/db/payment-sync";
import { IPromocodeCampaign } from "@/types/db/promocode-campaign";
import { IProductCategory } from "@/types/db/product-category";
import { ITimeSlot } from "@/types/db/timeslots";
import Dexie, { Table } from "dexie";
import { LogService } from "./logging";
import { CounterStatus, ITransaction } from "@/types/db/transaction";

// Type of an object to be inserted into Dexie db
export interface IReceiptCounterPayload {
  _id: string;
  groupId: string;
  reference: string;
  value: number;
  status: CounterStatus;
  syncStatus?: SyncStatus;
}

export interface IDexieFile {
  _id: string;
  file: any;
  customFieldId?: string;
  bookingUuid?: string;
  pssUuid?: string;
  syncStatus?: SyncStatus;
}

export interface IDexieUntypedTransaction {
  uuid: string;
  cashfloatUuid: string;
  bookingUuid: string;
  createdAtTimestamp: number;
  transaction: ITransaction;
  syncStatus: SyncStatus;
  attempts: number;
}

export interface IDexieCashTransfer {
  _id: string;
  cashTransfer: ICashTransfer;
}

export interface IDexieLog {
  _id: number; // auto increment index, used for sorting
  log: any;
}

export interface IDexiePicture {
  id: number;
  entityType: number;
  entityId: number;
  imageBlob: Blob;
  timestamp: string;
  metadata?: Record<string, any>; // optional field for additional data
}

export class DexieDatabase extends Dexie {
  // 'files' is added by dexie when declaring the stores()
  // We just tell the typing system this is the case
  public files!: Table<IDexieFile>;
  public cashTransfers!: Table<IDexieCashTransfer>;
  public logs!: Table<IDexieLog>;
  public cashPointClosures!: Table<IUntypedCashPointClosure>;
  public promocodeCampaigns!: Table<IPromocodeCampaign>;
  public pictures!: Table<IDexiePicture>;
  public receiptCounterPayload!: Table<IReceiptCounterPayload>;

  public orders!: Table<IPendingOrder>;

  // Completed orders' custom-field answer syncs queued for upload (files + answers).
  public customFieldAnswerSyncs!: Table<IPendingAnswerSync>;

  // Payments queued for retry, backing the @smeetz/payment-common offline hooks.
  public payments!: Table<IPendingPayment>;

  // Product categories and timeslots
  public productCategories!: Table<IProductCategory>;
  public timeslots!: Table<ITimeSlot>;

  // Transactions (migrated from RxDB)
  public untypedTransactions!: Table<IDexieUntypedTransaction>;

  constructor() {
    super("_dexie_smeetz-db");
    this.version(10)
      .stores({
        files: "_id, file",
        cashTransfers: "_id, cashTransfer",
        logs: "++_id, log",
        cashPointClosures: "_id, closure",
        promocodeCampaigns:
          "id, posConfigId, startDateTime, endDateTime, isPinned",
        pictures: "id, entityType, entityId, timestamp",
        receiptCounterPayload: "++id, groupId, reference, value, status, _id",

        untypedBookings:
          "_id, uuid, syncStatus, creationDateTime, booking.cashfloatUuid, [syncStatus+attempts], [syncStatus+creationDateTime]",
        updatedBookings: "_id, uuid, syncStatus, [syncStatus+attempts]",
      })
      .upgrade(async (trans) => {
        // No manual migration needed since we are only adding indexes,
        // Dexie will keep data and just build the new indexes.
        // Example: console.log("Migrating to version 9...");
        LogService.info(`Dexie DB: Migrating to version 10`);
      });

    // Version 11: Add product categories, timeslots, untypedTransactions tables (migrated from RxDB)
    //             Add syncStatus index to files and receiptCounterPayload tables
    //
    // productCategories indexes:
    // - id: primary key for direct lookups
    // - name: indexed field
    // - type: for filtering by PC type (e.g. Seated)
    // - *ticketGroups: multi-entry index for filtering by ticket group
    // - *barcodes: multi-entry index for fast barcode lookups
    // - *priceIds: multi-entry index for efficient price ID lookups
    // - *stockIds: multi-entry index for fast stock ID lookups
    // - seatingParentPSCId: for seating plan child lookups
    // - isRecurring: for filtering recurring vs non-recurring PCs
    //
    // timeslots indexes:
    // - id: primary key for direct lookups
    // - categoryId: for filtering by product category
    // - startDateTime, endDateTime: for date-based queries
    // - *ticketGroups: multi-entry index for filtering by ticket group
    // - [categoryId+startDateTime]: compound index for efficient range queries
    //   (endDateTime filtering is done in-memory since IndexedDB can only use
    //   the last key in a compound index for range queries)
    //
    // untypedTransactions indexes:
    // - uuid: primary key
    // - cashfloatUuid: for querying transactions by cash float (cash point closure)
    // - bookingUuid: for querying transactions by booking
    // - syncStatus: for finding Pending/Failed transactions to sync
    // - [syncStatus+createdAtTimestamp]: compound index for ordered sync queries
    // - [syncStatus+attempts]: compound index for retry logic with max attempts
    //
    this.version(12)
      .stores({
        files: "_id, syncStatus",
        cashTransfers: "_id, cashTransfer",
        logs: "++_id, log",
        cashPointClosures: "_id, closure",
        promocodeCampaigns:
          "id, posConfigId, startDateTime, endDateTime, isPinned",
        pictures: "id, entityType, entityId, timestamp",
        receiptCounterPayload: "++id, _id, syncStatus",

        untypedBookings:
          "_id, uuid, syncStatus, creationDateTime, booking.cashfloatUuid, [syncStatus+attempts], [syncStatus+creationDateTime]",
        updatedBookings: "_id, uuid, syncStatus, [syncStatus+attempts]",

        productCategories:
          "id, name, type, *ticketGroups, *barcodes, *skus, *priceIds, seatingParentPSCId, isRecurring, *stockIds",

        timeslots:
          "id, categoryId, startDateTime, endDateTime, *ticketGroups, [categoryId+startDateTime]",

        untypedTransactions:
          "uuid, cashfloatUuid, bookingUuid, syncStatus, createdAtTimestamp, [syncStatus+createdAtTimestamp], [syncStatus+attempts]",
      })
      .upgrade(async () => {
        LogService.info(
          `Dexie DB: Migrating to version 11 - Adding productCategories, timeslots, untypedTransactions tables; adding syncStatus to files and receiptCounterPayload`
        );
      });

    // Version 13: Migrate the offline sync queues from bookings to orders.
    // - Add the `orders` table (completed orders awaiting POST /orders/persist).
    // - Add the `customFieldAnswerSyncs` table (completed orders' custom-field answer
    //   + file uploads awaiting service.syncAnswers — the order-flow equivalent of the
    //   old booking files queue). Same index layout as `orders` for the drain.
    //   File blobs continue to live in the `files` table; this queue holds the answer
    //   DTOs + file refs so the drain can rebuild the upload map and retry.
    // - Add the `payments` table, backing the @smeetz/payment-common offline hooks
    //   (store/retrieve/delete + syncPaymentsFromQueue). Keyed by `${paymentId}_${method}`,
    //   indexed by `method` for the package's per-method retrieval.
    // - Drop the legacy untypedBookings / updatedBookings tables (set to null).
    this.version(13)
      .stores({
        orders:
          "_id, reference, syncStatus, creationDateTime, [syncStatus+attempts], [syncStatus+creationDateTime]",
        customFieldAnswerSyncs:
          "_id, orderId, syncStatus, creationDateTime, [syncStatus+attempts], [syncStatus+creationDateTime]",
        payments: "_id, paymentId, method, creationDateTime",
        untypedBookings: null,
        updatedBookings: null,
      })
      .upgrade(async () => {
        LogService.info(
          `Dexie DB: Migrating to version 13 - Adding orders + customFieldAnswerSyncs + payments sync tables; dropping untypedBookings and updatedBookings`
        );
      });

    // Handle blocked upgrades: when another tab holds an older DB version open,
    // log and close this connection so the app doesn't hang indefinitely.
    this.on("blocked", () => {
      console.warn(
        "Dexie DB upgrade blocked by another tab. Please close other tabs and refresh."
      );
    });

    // When this tab's connection is outdated (another tab upgraded the DB),
    // close the connection so the other tab can proceed.
    this.on("versionchange", (event) => {
      this.close();
      console.warn(
        "Dexie DB version changed by another tab, closing this connection."
      );
    });
  }
}

export const dexieDB = new DexieDatabase();
(window as any).dexieDB = dexieDB;
